import type { SwaggerOperation } from '../../types/mappingDocument';

export interface TagOption {
  tag: string;
  count: number;
}

/** Tags in spec order with how many operations carry each one. */
export function countOperationsByTag(tags: string[], operations: SwaggerOperation[]): TagOption[] {
  const counts = new Map<string, number>();
  for (const op of operations) for (const tag of op.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  return tags.map((tag) => ({ tag, count: counts.get(tag) ?? 0 }));
}

/** Operations matching any selected tag; no selected tags means every operation (mirrors the server filter). */
export function operationsInTags(operations: SwaggerOperation[], selectedTags: ReadonlySet<string>): SwaggerOperation[] {
  if (selectedTags.size === 0) return operations;
  return operations.filter((op) => op.tags.some((tag) => selectedTags.has(tag)));
}

export function searchOperations(operations: SwaggerOperation[], query: string): SwaggerOperation[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return operations;
  return operations.filter((op) => `${op.operationId} ${op.path} ${op.summary}`.toLowerCase().includes(needle));
}

/**
 * Picked operation IDs that are still in scope. Picks outside the selected tags would be dropped by the server's
 * tag filter anyway, so excluding them keeps the counts honest without changing the generated document.
 */
export function picksInScope(inScope: SwaggerOperation[], picks: ReadonlySet<string>): string[] {
  const ids = new Set<string>();
  for (const op of inScope) if (picks.has(op.operationId)) ids.add(op.operationId);
  return Array.from(ids);
}

/** "a, b,,c " → ["a", "b", "c"] */
export function parseList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

export function scopeSummary(inScopeCount: number, pickCount: number, tagCount: number): string {
  if (pickCount) return `${plural(pickCount, 'operation')} selected`;
  if (tagCount) return `${plural(inScopeCount, 'operation')} in ${plural(tagCount, 'tag')}`;
  return 'All operations';
}

const HTTP_URL = /^https?:\/\/\S+$/i;
export const isHttpUrl = (value: string) => HTTP_URL.test(value.trim());
